import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import {
  Target, MessageSquare, Users, Calendar, Building2, Handshake,
  BookOpen, FileText, FlaskConical, Send, UserCircle, Shield, Briefcase, ScanLine,
} from 'lucide-react';

/**
 * QuickActions — a compact grid of jump-off shortcuts.
 *
 * Used at the bottom of major content pages (Courses, Members, Research,
 * Partners, Forum, BriteLabs, Dashboard) so members always have a
 * one-tap path to the next destination without going back to the
 * Dashboard or hunting through nav menus.
 *
 *   <QuickActions user={user} exclude={['/forum']} />
 *
 * `exclude` accepts an array of routes to drop from the grid (typically
 * the current page) so the section doesn't link to itself.
 */
const ACTIONS = [
  { to: '/forum',     icon: MessageSquare, label: 'Forum',     bg: 'bg-brand-navy'  },
  { to: '/members',   icon: Users,         label: 'Members',   bg: 'bg-brand-sage'  },
  { to: '/events',    icon: Calendar,      label: 'Events',    bg: 'bg-orange-600'  },
  { to: '/companies', icon: Building2,     label: 'Companies', bg: 'bg-teal-600'    },
  { to: '/partners',  icon: Handshake,     label: 'Partners',  bg: 'bg-cyan-600'    },
  { to: '/courses',   icon: BookOpen,      label: 'Courses',   bg: 'bg-blue-600'    },
  { to: '/learning',  icon: FileText,      label: 'Learning',  bg: 'bg-amber-600'   },
  { to: '/jobs',      icon: Briefcase,     label: 'Jobs',      bg: 'bg-indigo-600'  },
  { to: '/britelabs', icon: FlaskConical,  label: 'BriteLabs', bg: 'bg-purple-600'  },
  { to: '/messages',  icon: Send,          label: 'Messages',  bg: 'bg-emerald-600' },
  { to: '/profile',   icon: UserCircle,    label: 'My Profile', bg: 'bg-slate-600'  },
];

export default function QuickActions({ user, exclude = [], className = '' }) {
  const excludeSet = new Set(exclude);
  const items = ACTIONS.filter(a => !excludeSet.has(a.to));
  if (user?.role === 'admin' && !excludeSet.has('/admin')) {
    items.push({ to: '/admin/check-in', icon: ScanLine, label: 'QR Check-In', bg: 'bg-rose-600' });
    items.push({ to: '/admin', icon: Shield, label: 'Admin', bg: 'bg-slate-700' });
  }

  return (
    <div className={`mt-6 md:mt-8 ${className}`} data-testid="quick-actions-section">
      <h3 className="text-xs md:text-sm font-bold text-slate-700 mb-2 md:mb-3 flex items-center gap-2">
        <Target className="h-4 w-4 text-slate-400" /> Quick Actions
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3" data-testid="quick-actions-grid">
        {items.map(a => (
          <Link key={a.to} to={a.to}>
            <Card className="p-2.5 md:p-3 border-0 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer text-center group"
              data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className={`w-8 h-8 md:w-10 md:h-10 ${a.bg} rounded-xl flex items-center justify-center mx-auto mb-1 md:mb-2 group-hover:scale-110 transition-transform`}>
                <a.icon className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <p className="text-[10px] md:text-xs font-medium text-slate-600">{a.label}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
